import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BookOpen, Plus, Edit, Eye, Calendar, Layers, Trash2 } from 'lucide-react';
import { AdminLayout } from '../components/AdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authHeader = () => {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : {};
};

// ── Delete Retired Issues button ─────────────────────────────────────────────
function DeleteRetiredButton({ onDeleted }) {
  const [state, setState] = useState('idle'); // idle | confirm | running | done | error
  const [result, setResult] = useState(null);

  const run = async () => {
    setState('running');
    setResult(null);
    try {
      const res = await fetch(`${API}/admin/delete-retired-issues`, {
        method: 'POST',
        headers: authHeader(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setResult(data);
      setState('done');
      if (onDeleted) onDeleted();
    } catch (e) {
      setResult({ error: e.message });
      setState('error');
    }
  };

  if (state === 'confirm') {
    return (
      <div className="flex items-center gap-2">
        <span className="text-xs text-red-600 font-medium">Delete retired issues?</span>
        <button
          data-testid="delete-retired-confirm-btn"
          onClick={run}
          className="px-3 py-1.5 text-xs rounded-lg bg-red-600 text-white font-semibold hover:bg-red-700 transition-colors"
        >
          Yes, delete
        </button>
        <button
          data-testid="delete-retired-cancel-btn"
          onClick={() => setState('idle')}
          className="px-3 py-1.5 text-xs rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        data-testid="delete-retired-btn"
        onClick={() => {
          if (state === 'running') return;
          if (state === 'done' || state === 'error') { setState('idle'); setResult(null); return; }
          setState('confirm');
        }}
        disabled={state === 'running'}
        className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs rounded-lg border font-medium transition-colors whitespace-nowrap
          ${state === 'done'  ? 'border-green-300 text-green-700 bg-green-50'
          : state === 'error' ? 'border-red-300 text-red-700 bg-red-50'
          : 'border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300'}`}
      >
        <Trash2 size={13} />
        {state === 'running' ? 'Deleting…' : state === 'done' ? 'Cleanup ✓' : state === 'error' ? 'Error ✗' : 'Delete Retired Issues'}
      </button>

      {result && state === 'done' && (
        <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-3 w-72 text-xs text-gray-700">
          <p className="font-semibold mb-2 text-green-700">Cleanup complete</p>
          <p>Issues deleted: <b>{result.archived_issues_deleted?.length ?? 0}</b></p>
          {result.archived_issues_deleted?.length > 0 && (
            <ul className="mt-1 space-y-0.5 text-gray-500">
              {result.archived_issues_deleted.map(i => (
                <li key={i.id}>• {i.slug} ({i.pages_deleted} pages)</li>
              ))}
            </ul>
          )}
          {result.archived_issues_skipped?.length > 0 && (
            <p className="mt-1 text-yellow-600">Skipped (active links): <b>{result.archived_issues_skipped.length}</b></p>
          )}
          <p className="mt-1">Orphan pages cleaned: <b>{result.orphan_pages_deleted ?? 0}</b></p>
          <p>Total pages removed: <b>{result.total_pages_deleted ?? 0}</b></p>
          <button onClick={() => { setState('idle'); setResult(null); }} className="mt-2 text-gray-400 underline">dismiss</button>
        </div>
      )}
      {result && state === 'error' && (
        <div className="absolute right-0 top-full mt-1 bg-white border border-red-200 rounded-lg shadow-lg z-50 p-3 w-48 text-xs text-red-700">
          Error: {result.error}
          <button onClick={() => { setState('idle'); setResult(null); }} className="ml-2 underline">dismiss</button>
        </div>
      )}
    </div>
  );
}


const STATUS_CONFIG = {
  draft:     { label: 'Draft',      bg: 'bg-gray-100',   text: 'text-gray-600' },
  review:    { label: 'In Review',  bg: 'bg-blue-100',   text: 'text-blue-700' },
  scheduled: { label: 'Scheduled',  bg: 'bg-yellow-100', text: 'text-yellow-700' },
  published: { label: 'Published',  bg: 'bg-green-100',  text: 'text-green-700' },
  archived:  { label: 'Archived',   bg: 'bg-red-100',    text: 'text-red-600' },
};

export const MagazineList = () => {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token) { navigate('/admin/login'); return; }
    loadIssues();
  }, [navigate]);

  const loadIssues = async () => {
    try {
      const response = await axios.get(`${API}/magazine?issue_status=all`);
      const all = response.data;

      // Ordering: July flagship first, then monthly drafts Jan–Jun by issue_number, then legacy bridge last
      const july      = all.filter(i => i.slug === 'july-2026');
      const monthly   = all.filter(i => i.slug !== 'july-2026' && i.slug !== 'jan-feb-2026').sort((a, b) => (a.issue_number || 99) - (b.issue_number || 99));
      const bridge    = all.filter(i => i.slug === 'jan-feb-2026');
      setIssues([...july, ...monthly, ...bridge]);
    } catch (err) {
      console.error('Failed to load issues:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout title="Magazine Manager" breadcrumb="Issues">
      <div data-testid="magazine-list-page">
        {/* Top actions */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl font-bold text-gray-900" data-testid="issues-count">
              {issues.length} Issue{issues.length !== 1 ? 's' : ''}
            </h2>
            <p className="text-gray-500 text-sm mt-1">Manage Opportunity Post Monthly editions</p>
          </div>
        <div className="flex items-center gap-3">
          <DeleteRetiredButton onDeleted={loadIssues} />
          <Link
            to="/admin/magazine/new"
            className="inline-flex items-center gap-2 bg-[#0E2E59] text-white px-5 py-2.5 rounded-lg font-semibold hover:bg-[#1a4d8f] transition-colors shadow"
            data-testid="create-issue-btn"
          >
            <Plus size={18} /> New Issue
          </Link>
        </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24" data-testid="issues-loading">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#0E2E59]" />
          </div>
        ) : issues.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="space-y-4" data-testid="issues-table">
            {issues.map((issue, idx) => {
              const isBridge = issue.slug === 'jan-feb-2026';
              const prevIsBridge = idx > 0 && issues[idx - 1].slug !== 'jan-feb-2026' && isBridge;
              return (
                <React.Fragment key={issue.id}>
                  {prevIsBridge && (
                    <div className="flex items-center gap-3 pt-2" data-testid="bridge-separator">
                      <div className="h-px flex-1 bg-gray-200" />
                      <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Published Bridge — Temporary</span>
                      <div className="h-px flex-1 bg-gray-200" />
                    </div>
                  )}
                  <IssueRow issue={issue} />
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

const IssueRow = ({ issue }) => {
  const status = STATUS_CONFIG[issue.issue_status] || STATUS_CONFIG.draft;
  return (
    <div
      className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4 hover:shadow-md transition-shadow"
      data-testid="issue-row"
    >
      {/* Cover or placeholder */}
      <div
        className="w-14 h-18 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center"
        style={{ width: 52, height: 70, background: 'linear-gradient(135deg,#081D30,#006E6A)' }}
        data-testid="issue-cover-thumb"
      >
        {issue.cover_image_url ? (
          <img src={issue.cover_image_url} alt={issue.title} className="w-full h-full object-cover" />
        ) : (
          <span className="text-white font-black text-xs">OP</span>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-3 flex-wrap">
          <h3 className="font-bold text-gray-900 text-base truncate" data-testid="issue-title">{issue.title}</h3>
          {issue.slug === 'july-2026' && (
            <span className="text-xs bg-[#a87820] text-white px-2 py-0.5 rounded font-bold" data-testid="flagship-badge">FLAGSHIP</span>
          )}
          {issue.is_latest && (
            <span className="text-xs bg-[#008C86] text-white px-2 py-0.5 rounded font-bold" data-testid="latest-badge">LATEST</span>
          )}
          <span className={`text-xs px-2 py-0.5 rounded font-semibold ${status.bg} ${status.text}`} data-testid="status-badge">
            {status.label}
          </span>
        </div>
        <div className="flex items-center gap-4 mt-1 text-sm text-gray-500 flex-wrap">
          <span className="flex items-center gap-1"><Calendar size={13} />{issue.issue_date}</span>
          <span className="flex items-center gap-1"><Layers size={13} />{issue.page_count || 40} pages</span>
          {issue.issue_number && <span>VOL. {issue.volume_number || 6} NO. {String(issue.issue_number).padStart(2,'0')}</span>}
          {issue.issue_theme && <span className="italic truncate max-w-xs">{issue.issue_theme}</span>}
        </div>
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        <Link
          to={`/magazine/${issue.slug}`}
          className="p-2 text-gray-400 hover:text-[#008C86] rounded-lg hover:bg-gray-50 transition-colors"
          title="View public page"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="view-public-btn"
        >
          <Eye size={18} />
        </Link>
        <Link
          to={`/admin/magazine/${issue.id}/pages`}
          className="p-2 text-gray-400 hover:text-[#0E2E59] rounded-lg hover:bg-gray-50 transition-colors"
          title="Manage pages"
          data-testid="manage-pages-btn"
        >
          <BookOpen size={18} />
        </Link>
        <Link
          to={`/admin/magazine/${issue.id}/edit`}
          className="flex items-center gap-1.5 bg-[#0E2E59] text-white text-sm px-3 py-1.5 rounded-lg hover:bg-[#1a4d8f] transition-colors font-medium"
          data-testid="edit-issue-btn"
        >
          <Edit size={14} /> Edit
        </Link>
      </div>
    </div>
  );
};

const EmptyState = () => (
  <div className="text-center py-24 bg-white rounded-xl border border-gray-100" data-testid="empty-state">
    <BookOpen size={48} className="mx-auto text-gray-300 mb-4" />
    <h3 className="text-xl font-bold text-gray-700 mb-2">No issues yet</h3>
    <p className="text-gray-400 mb-6">Create your first Opportunity Post Monthly issue to get started.</p>
    <Link
      to="/admin/magazine/new"
      className="inline-flex items-center gap-2 bg-[#0E2E59] text-white px-5 py-2.5 rounded-lg font-semibold hover:bg-[#1a4d8f] transition-colors"
      data-testid="empty-create-btn"
    >
      <Plus size={18} /> Create First Issue
    </Link>
  </div>
);
